import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Save } from "lucide-react";
import StageShell from "@/components/StageShell";
import { Button } from "@/components/ui/button";
import DraftEditor from "@/components/draft/DraftEditor";
import CoveragePanel from "@/components/draft/CoveragePanel";
import { useRun } from "@/hooks/useRun";
import { api, errMsg } from "@/lib/api";

export default function Stage5({ job, refresh }) {
  const [ver, setVer] = useState(null);
  const [content, setContent] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [bank, setBank] = useState([]);
  const [dirty, setDirty] = useState(false);

  const load = useCallback(async () => {
    if (!job.current_version_id) return;
    const [v, b] = await Promise.all([api.get(`/versions/${job.current_version_id}`), api.get(`/jobs/${job.id}/bullets`)]);
    setVer(v.data); setContent(v.data.content); setAnalysis(v.data.analysis); setBank(b.data); setDirty(false);
  }, [job.current_version_id, job.id]);
  useEffect(() => { load(); }, [load]);
  const runHook = useRun(job.id, 5, () => refresh());

  const change = (c) => { setContent(c); setDirty(true); };
  const save = async (c = content, note) => {
    try {
      const { data } = await api.put(`/versions/${ver.id}/content`, { content: c, change: note });
      setAnalysis(data.analysis); setDirty(false); toast.success("Draft saved");
      refresh();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const decide = (field, idx, val) => {
    const a = { ...content.approvals };
    if (field === "work_authorization") a.work_authorization = val;
    else a[field] = a[field].map((x, i) => (i === idx ? val : x));
    const c = { ...content, approvals: a };
    setContent(c);
    save(c, `${val ? "approved" : "rejected"} ${field}`);
  };

  return (
    <StageShell job={job} stage={5} refresh={refresh} runHook={runHook}
      runs={[{ action: "draft", label: ver ? "Generate new draft version" : "Generate draft", testid: "draft-generate-button" }]}
      extra={ver && <Button data-testid="draft-save-button" variant={dirty ? "default" : "outline"} disabled={!dirty || ver.locked} onClick={() => save()}
        className={dirty ? "bg-emerald-600 hover:bg-emerald-500 text-white" : ""}><Save size={14} className="mr-1.5" />{dirty ? "Save changes" : "Saved"}</Button>}>
      {!content ? <div className="card-surface text-sm text-muted-foreground">Generate the draft once the positioning angle is chosen.</div> : (
        <div className="grid grid-cols-[minmax(0,1fr)_380px] gap-5 items-start">
          <div>
            <div className="text-xs text-muted-foreground mb-2">Version <span className="font-mono text-slate-300">{ver.label}</span>{ver.locked && " · locked (client approved)"}</div>
            <DraftEditor content={content} onChange={change} bank={bank} locked={ver.locked} onDecide={decide} />
          </div>
          <CoveragePanel analysis={analysis} gap={job.gap} />
        </div>
      )}
    </StageShell>
  );
}
